/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useEffect } from 'react'

interface UseResizingReturn {
  leftWidth: number
  rightWidth: number
  isResizingLeft: boolean
  isResizingRight: boolean
  setLeftWidth: (width: number) => void
  setRightWidth: (width: number) => void
  setIsResizingLeft: (resizing: boolean) => void
  setIsResizingRight: (resizing: boolean) => void
  handleMouseMove: (e: MouseEvent) => void
  handleMouseUp: () => void
}

export function useResizing(
  containerRef: React.RefObject<HTMLDivElement | null>
): UseResizingReturn {
  const [leftWidth, setLeftWidth] = useState(260)
  const [rightWidth, setRightWidth] = useState(520)
  const [isResizingLeft, setIsResizingLeft] = useState(false)
  const [isResizingRight, setIsResizingRight] = useState(false)

  const handleMouseMove = (e: MouseEvent) => {
    if (!containerRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()

    if (isResizingLeft) {
      const newWidth = e.clientX - containerRect.left
      // Increased max width from 400 to 600 to accommodate deeper folder structures
      if (newWidth >= 200 && newWidth <= 600) {
        setLeftWidth(newWidth)
      }
    }

    if (isResizingRight) {
      const newWidth = containerRect.right - e.clientX
      if (newWidth >= 400 && newWidth <= 800) {
        setRightWidth(newWidth)
      }
    }
  }

  const handleMouseUp = () => {
    setIsResizingLeft(false)
    setIsResizingRight(false)
  }

  useEffect(() => {
    if (isResizingLeft || isResizingRight) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isResizingLeft, isResizingRight])

  return {
    leftWidth,
    rightWidth,
    isResizingLeft,
    isResizingRight,
    setLeftWidth,
    setRightWidth,
    setIsResizingLeft,
    setIsResizingRight,
    handleMouseMove,
    handleMouseUp,
  }
}

